// src/layout/customer/CompletedProject/CompletedCard.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2, // Builder Company
  ClipboardList, // Project Name
  MapPin, // Location
  CalendarDays, // Dates
  IndianRupee, // Budget
  FileText, // Document Icon
  MessageCircle, // Chat Icon
  CreditCard, // Payment Icon
  UserCircle, // Builder Name Icon
  BadgeCheck, // Builder ID Icon
  Phone, // Builder Contact
  Loader2, // Loading Icon
  CheckBadgeIcon, // Completed Status Icon
  Eye, // View Icon for documents
  X, // Close icon for modals
  AlertTriangle, // Error icon for document fetch
  ExternalLink, // Added for View Details
} from "lucide-react";
import useProjectStatus from "../../../store/builders/useProjectStatus"; // Shared store for fetching projects
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";

// ... (helper functions remain the same)
// Helper function to capitalize the first letter
const capitalize = (s) => (s && s[0].toUpperCase() + s.slice(1)) || "";

// Helper function to format currency
const formatCurrency = (amount) => {
  if (amount == null || isNaN(Number(amount))) return "N/A";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(amount));
};

// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch (e) {
    return "Invalid Date";
  }
};

// Helper to get file icon
const getFileIcon = (fileName) => {
  if (!fileName)
    return <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />;
  const extension = fileName.split(".").pop()?.toLowerCase();
  if (["pdf"].includes(extension))
    return <FileText className="w-4 h-4 text-red-500 flex-shrink-0" />;
  if (["jpg", "jpeg", "png", "webp", "gif"].includes(extension))
    return <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />;
  return <FileText className="w-4 h-4 text-gray-500 flex-shrink-0" />;
};

const CompletedCard = () => {
  const navigate = useNavigate();
  const {
    projects: completedProjects, // Renamed for clarity
    isLoading: projectsLoading,
    fetchProjects,
    checkCompletionDocumentsStatus, // For fetching doc status for the modal
    error: projectsError,
  } = useProjectStatus();

  const [isDocsModalOpen, setIsDocsModalOpen] = useState(false);
  const [selectedProjectForDocs, setSelectedProjectForDocs] = useState(null);
  const [docsModalData, setDocsModalData] = useState(null);
  const [isFetchingDocs, setIsFetchingDocs] = useState(false);
  const [docsModalError, setDocsModalError] = useState(null);

  useEffect(() => {
    fetchProjects("completed");
  }, [fetchProjects]);

  const fetchDocsForModal = async (projectId) => {
    setIsFetchingDocs(true);
    setDocsModalError(null);
    setDocsModalData(null);
    try {
      const result = await checkCompletionDocumentsStatus(projectId); // Using the builder's store function
      if (result && result.success) {
        setDocsModalData(result);
      } else {
        setDocsModalError(
          result?.message || "Could not fetch document status."
        );
        toast.error(result?.message || "Could not fetch document status.");
      }
    } catch (error) {
      setDocsModalError("An error occurred while fetching document status.");
      console.error("Docs Fetch Error:", error);
      toast.error("Error fetching documents.");
    } finally {
      setIsFetchingDocs(false);
    }
  };

  const handleViewDocuments = (project) => {
    setSelectedProjectForDocs(project);
    setIsDocsModalOpen(true);
    fetchDocsForModal(project.id);
  };

  const closeDocsModal = () => {
    setIsDocsModalOpen(false);
    setSelectedProjectForDocs(null);
    setDocsModalData(null);
    setDocsModalError(null);
  };

  if (projectsError) {
    return <p className="text-center text-red-500 p-4">{projectsError}</p>;
  }

  if (projectsLoading && completedProjects.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!projectsLoading && completedProjects.length === 0) {
    return (
      <div className="text-center text-gray-600 p-8 text-sm min-h-[60vh] flex flex-col justify-center items-center">
        <img
          src="/assets/check.png"
          alt="No Completed Projects"
          className="w-20 h-auto mb-4 opacity-70"
        />
        <p className="text-lg font-semibold">No Projects Completed Yet</p>
        <p>Your completed projects will appear here once finalized.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-2 sm:px-4 py-4">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {completedProjects.map((project) => (
          <div
            key={project.id}
            className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 flex flex-col transition-all duration-300 hover:shadow-xl h-full"
          >
            {/* Builder Info */}
            <div className="mb-3 pb-3 border-b border-gray-100">
              <div className="flex items-center gap-3 mb-1.5 min-w-0">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center overflow-hidden border border-green-200 flex-shrink-0">
                  <Building2 className="w-5 h-5 text-green-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3
                    className="text-sm sm:text-base font-semibold text-gray-800 truncate sm:whitespace-normal sm:break-words"
                    title={project.builder_company}
                  >
                    {project.builder_company || "N/A"}
                  </h3>
                  {project.builder_name && (
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <UserCircle className="w-3 h-3 text-gray-400" />
                      <span className="truncate">{project.builder_name}</span>
                    </p>
                  )}
                </div>
              </div>
              <div className="space-y-1 text-xs">
                <div className="flex items-center gap-2 text-gray-600">
                  <BadgeCheck className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                  <span>
                    Ducktail ID: {project.builder_ducktail_id || "N/A"}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Phone className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <span>
                    {project.builder_contact
                      ? `+91 ${project.builder_contact}`
                      : "N/A"}
                  </span>
                </div>
              </div>
            </div>

            {/* Project Details */}
            <div className="flex-grow">
              <div className="flex items-center gap-2 mb-2">
                <ClipboardList className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <h4
                  className="text-xs font-medium text-gray-700 truncate"
                  title={project.project_name}
                >
                  {project.project_name || "Project Name N/A"}
                </h4>
              </div>
              <div className="space-y-1.5 text-xs text-gray-600 mb-3">
                <div className="flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <span className="truncate" title={project.project_location}>
                    {project.project_location || "Location N/A"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <IndianRupee className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <span>{formatCurrency(project.project_budget)}</span>
                </div>
                <div className="grid grid-cols-2 gap-x-2">
                  <div className="flex items-center gap-1.5">
                    <CalendarDays className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                    <div>
                      <p className="text-[10px] text-gray-400">Start Date</p>
                      <p className="font-medium">
                        {formatDate(project.starting_date)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <CalendarDays className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                    <div>
                      <p className="text-[10px] text-gray-400">
                        Completed Date
                      </p>
                      <p className="font-medium">
                        {formatDate(project.completed_date)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              {/* Status */}
              <div className="mb-3">
                <div className="flex items-center gap-1.5 text-xs">
                  <BadgeCheck className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span className="text-gray-500">Status:</span>
                  <span className="font-semibold capitalize px-1.5 py-0.5 rounded-full text-white text-[9px] bg-green-500">
                    {capitalize(project.status) || "N/A"}
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-auto pt-3 border-t border-gray-100 grid grid-cols-3 gap-2">
              <button
                onClick={() =>
                  navigate(`/customer/project/${project.id}/payment`)
                }
                className="flex-1 px-2 py-1.5 bg-green-50 text-green-600 rounded-md text-[10px] font-medium hover:bg-green-100 transition-colors flex items-center justify-center gap-1"
                title="View Payment History"
              >
                <CreditCard className="w-3 h-3" /> Payment
              </button>
              <button
                onClick={() => handleViewDocuments(project)}
                className="flex-1 px-2 py-1.5 bg-purple-50 text-purple-600 rounded-md text-[10px] font-medium hover:bg-purple-100 transition-colors flex items-center justify-center gap-1"
                title="View Final Documents"
              >
                <FileText className="w-3 h-3" /> Documents
              </button>
              <button
                onClick={() => navigate(`/master-data/${project.id}`)}
                className="flex-1 px-2 py-1.5 bg-gray-100 text-gray-700 rounded-md text-[10px] font-medium hover:bg-gray-200 transition-colors flex items-center justify-center gap-1"
                title="View All Project Details"
              >
                <ExternalLink className="w-3 h-3" /> Details
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Documents Modal (Same as in CompleteRequestCard) */}
      <AnimatePresence>
        {isDocsModalOpen && selectedProjectForDocs && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
            onClick={closeDocsModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              className="bg-white rounded-xl shadow-2xl p-5 sm:p-6 w-full max-w-lg relative flex flex-col max-h-[90vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center pb-3 border-b border-gray-200 mb-4 flex-shrink-0">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-purple-500" />
                  Completion Documents
                </h3>
                <button
                  onClick={closeDocsModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
                  aria-label="Close documents modal"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="text-xs text-gray-500 mb-3">
                Project: {selectedProjectForDocs.project_name || "N/A"}
              </div>
              <div className="flex-grow overflow-y-auto pr-2 -mr-2 custom-scrollbar-purple">
                {isFetchingDocs ? (
                  <div className="flex justify-center items-center h-40">
                    <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
                    <span className="ml-2 text-gray-500 text-sm">
                      Loading documents...
                    </span>
                  </div>
                ) : docsModalError ? (
                  <div className="text-center py-6 px-4 bg-red-50 text-red-700 rounded-md border border-red-200 text-sm flex flex-col items-center gap-2">
                    <AlertTriangle className="w-6 h-6 text-red-500" />{" "}
                    {docsModalError}
                  </div>
                ) : docsModalData?.documents &&
                  docsModalData.documents.length > 0 ? (
                  <div className="space-y-2.5">
                    {docsModalData.documents.map((doc, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg border border-gray-100 text-xs"
                      >
                        <div className="flex items-center gap-2 flex-grow mr-2">
                          {getFileIcon(doc.url)}
                          <span className="font-medium text-gray-700">
                            {doc.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {doc.uploaded ? (
                            <span className="text-green-600 font-semibold text-[9px] bg-green-100 px-1.5 py-0.5 rounded-full">
                              Uploaded
                            </span>
                          ) : (
                            <span className="text-orange-600 font-semibold text-[9px] bg-orange-100 px-1.5 py-0.5 rounded-full">
                              Missing
                            </span>
                          )}
                          {doc.uploaded && doc.url ? (
                            <a
                              href={doc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline text-[10px] font-medium flex items-center gap-0.5"
                              title={`View ${doc.name}`}
                            >
                              <Eye className="w-3 h-3" /> View
                            </a>
                          ) : (
                            <span className="text-gray-400 text-[10px] italic">
                              Not Uploaded
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-6 text-sm">
                    No final documents available for this project.
                  </p>
                )}
              </div>
              <div className="mt-5 pt-4 border-t border-gray-200 flex justify-end flex-shrink-0">
                <button
                  onClick={closeDocsModal}
                  className="px-4 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-xs font-medium"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style jsx>{`
        .custom-scrollbar-purple::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar-purple::-webkit-scrollbar-track {
          background: #f3e8ff;
          border-radius: 10px;
        } /* Light purple */
        .custom-scrollbar-purple::-webkit-scrollbar-thumb {
          background: #c084fc;
          border-radius: 10px;
        } /* Purple-400 */
        .custom-scrollbar-purple::-webkit-scrollbar-thumb:hover {
          background: #a855f7;
        } /* Purple-500 */
      `}</style>
    </div>
  );
};

export default CompletedCard;
